import { REPEATER_LINE_INPUT_FIELD_KEYS } from './repeater/gen1RepeaterLineFieldKeys';

/** 明細フィールド値を trim した文字列にする。 */
export function trimRepeaterField(value) {
    return (value ?? '').toString().trim();
}

/** Search/Register 突合用の (Model, Serial) キー。 */
export function makeModelSerialKey(baseModel, serialNumber) {
    return `${trimRepeaterField(baseModel)}\t${trimRepeaterField(serialNumber)}`;
}

/** SO# が同一か（大文字小文字無視。どちらか空なら false）。 */
export function orderNumbersEqual(a, b) {
    const left = trimRepeaterField(a);
    const right = trimRepeaterField(b);
    if (!left || !right) {
        return false;
    }
    return left.toUpperCase() === right.toUpperCase();
}

/** ADD セクション行か（Exchange は含まず、手動 Add と Search 由来 ADD の2種）。 */
export function isAddSectionRepeaterLine(line) {
    if (!line) return false;
    const op = trimRepeaterField(line.OperationType).toUpperCase();
    return op === 'ADD' && line.ExchangePairId == null;
}

/** Search 由来 ADD か（ADD セクション・OrderNumber あり）。 */
export function isSearchAddRepeaterLine(line) {
    if (!isAddSectionRepeaterLine(line)) return false;
    return trimRepeaterField(line.OrderNumber) !== '';
}

/** 手動 Add Row か（ADD セクション・OrderNumber なし）。 */
export function isManualAddRepeaterLine(line) {
    if (!isAddSectionRepeaterLine(line)) return false;
    return trimRepeaterField(line.OrderNumber) === '';
}

/** Exchange ペアか。 */
export function isExchangeRepeaterLine(line) {
    return line != null && line.ExchangePairId != null;
}

/** Remove 単体か（Exchange ペア外）。 */
export function isStandaloneRemoveRepeaterLine(line) {
    if (!line) return false;
    const op = trimRepeaterField(line.OperationType).toUpperCase();
    return op === 'REMOVE' && line.ExchangePairId == null;
}

/** 行種別を返す。 @returns {'searchAdd'|'manualAdd'|'exchange'|'remove'|'unknown'} */
export function getRepeaterLineKind(line) {
    if (isSearchAddRepeaterLine(line)) return 'searchAdd';
    if (isManualAddRepeaterLine(line)) return 'manualAdd';
    if (isExchangeRepeaterLine(line)) return 'exchange';
    if (isStandaloneRemoveRepeaterLine(line)) return 'remove';
    return 'unknown';
}

/** プレースホルダ以外の入力がある行か。 */
export function hasRepeaterLineInput(line) {
    if (!line) return false;
    return REPEATER_LINE_INPUT_FIELD_KEYS.some(
        (key) => trimRepeaterField(line[key]) !== ''
    );
}

/** Search 由来 ADD が 1 行以上あるか。 */
export function hasSearchAddRepeaterLines(lines) {
    return (lines ?? []).some(isSearchAddRepeaterLine);
}

/**
 * ADD セクションを追加順で再採番する（購入済み useEffect 用）。
 * - 既存 ADD 行は配列順（追加された順）を維持。手動・Searchは並べ替えはしない
 * - RepeaterQty に足りない分は末尾に空の手動 Add スロットを追加
 * Register 時の旧 Search 除去・詰め・新 Search 連結は mergeRepeaterLinesAfterRegister が担当する。
 *
 * @returns {{ lines: Object[], nextLineIndex: number }}
 */
export function rebuildAddSectionLinesInAdditionOrder(
    addSectionLines,
    repeaterQty,
    startLineIndex = 1
) {
    const total = Math.max(0, Number(repeaterQty) || 0);
    const newLines = [];
    let lineIndex = startLineIndex;

    for (const line of addSectionLines ?? []) {
        if (lineIndex > 100) break;
        if (newLines.length >= total) break;
        newLines.push({
            ...line,
            LineSequenceNumber: lineIndex,
        });
        lineIndex++;
    }
    const needNewSlots = Math.max(0, total - newLines.length);
    for (let i = 0; i < needNewSlots && lineIndex <= 100; i++) {
        newLines.push({
            LineSequenceNumber: lineIndex,
            OperationType: 'ADD',
            ExchangePairId: null,
            ExchangeSystemId: '',
            OrderNumber: '',
            BaseModel: '',
            SerialNumber: '',
        });
        lineIndex++;
    }
    return { lines: newLines, nextLineIndex: lineIndex };
}

/** ADD ブロックを手動行と Search 行に分ける。 */
export function partitionAddSectionManualAndSearch(lines) {
    const addSection = (lines ?? []).filter(isAddSectionRepeaterLine);
    return {
        manualLines: addSection.filter(isManualAddRepeaterLine),
        searchLines: addSection.filter(isSearchAddRepeaterLine),
    };
}

/** 指定 SO# の Search 由来 ADD 行のみ返す。 */
export function filterSearchAddLinesForOrderNumber(lines, orderNumber) {
    return (lines ?? []).filter(
        (line) =>
            isSearchAddRepeaterLine(line) &&
            orderNumbersEqual(line.OrderNumber, orderNumber)
    );
}

/** 指定 SO# 以外の Search 由来 ADD 行のみ返す。 */
export function filterSearchAddLinesNotMatchingOrderNumber(lines, orderNumber) {
    return (lines ?? []).filter(
        (line) =>
            isSearchAddRepeaterLine(line) &&
            !orderNumbersEqual(line.OrderNumber, orderNumber)
    );
}

/** ADD ブロック以外（Exchange / Remove）の行のみ返す。 */
export function filterNonAddSectionRepeaterLines(lines) {
    return (lines ?? []).filter((line) => !isAddSectionRepeaterLine(line));
}

/** 単一 SO# 運用: 操作 SO# 以外の Search ADD を除去。orderNumber 空ならそのまま返す。 */
export function filterRepeaterLinesForSingleSoOperation(lines, orderNumber) {
    const opOrder = trimRepeaterField(orderNumber ?? '');
    if (!opOrder) {
        return lines ?? [];
    }
    return (lines ?? []).filter(
        (line) =>
            !isSearchAddRepeaterLine(line) ||
            orderNumbersEqual(line.OrderNumber, opOrder)
    );
}
